import {
  CssVars,
  CssVarDefinitions,
  CssVarReferences,
  CssVar,
  CssVarRef,
} from '../theme/types';

const toCssVar = (key: string): CssVar =>
  key.startsWith('--') ? (key as CssVar) : `--${key}`;

/**
 * Resolves a formatter-or-prefix argument into a consistent key-to-CssVar mapping function.
 * - If nothing is provided, keys are converted to CSS variables as-is.
 * - If a string is provided, keys are prefixed with it (e.g. `prefix-key`).
 * - If a custom formatter function is provided, it is used to transform the key before conversion.
 */
const normalizeFormatter = (
  formatterOrPrefix?: ((key: string) => string) | string
): ((key: string) => CssVar) => {
  if (!formatterOrPrefix) {
    return (key): CssVar => toCssVar(key);
  } else if (typeof formatterOrPrefix === 'string') {
    return (key): CssVar => toCssVar(`${formatterOrPrefix}-${key}`);
  }
  return (key) => toCssVar(formatterOrPrefix(key));
};

/**
 * Creates a type-safe CSS variables utility object.
 *
 * This utility helps manage CSS custom properties (CSS variables) in a type-safe manner.
 * It generates CSS var definitions, references, and provides helpers for dynamic value setting.
 *
 * @template T - Object type defining the CSS variables and their values
 * @param vars - Optional object containing CSS variable names as keys and their values (string, number, or undefined)
 * @param formatterOrPrefix - Optional function or prefix to format CSS variable names. Defaults to identity function.
 *                            The formatted name will be prefixed with `--` automatically.
 *
 * @returns A `CssVars<T>` object containing:
 *   - `defs`: CSS var definitions that can be passed directly to HTML style tag
 *   - `varNames`: CSS variable names without the `--` prefix (e.g., `inset-pt`)
 *   - `refs`: Type-safe CSS var references in the format `var(--varName)`
 *   - `getters`: Functions that accept default values and return `var(--varName, defaultValue)`
 *   - `setValues`: Function to generate CSS var definitions in a type-safe way
 *
 * @example
 * ```ts
 * const theme = cssVars({
 *   primaryColor: '#0066cc',
 *   spacing: 16,
 *   borderRadius: 4
 *   dynamicWidth: undefined // You can pass `undefined` here and set it later via `setValues`. Useful, when the actual value is known during runtime
 * });
 *
 * // theme.defs -> { '--primaryColor': '#0066cc', '--spacing': 16, '--borderRadius': 4 }
 * // Use definitions in style tag
 * <div style={theme.defs}>
 *   <div style={{ color: theme.refs.primaryColor }} /> // theme.refs.primaryColor -> 'var(--primaryColor)'
 * </div>
 *
 * // Use getter with default value
 * const color = theme.getters.primaryColor('#000000'); // color -> 'var(--primaryColor, #000000)'
 *
 * // Set values dynamically
 * const darkTheme = theme.setValues({ primaryColor: '#ffffff' }); // darkTheme -> { '--primaryColor': '#ffffff' }
 * ```
 */
export const cssVars = <T extends Record<string, string | number | undefined>>(
  vars?: T,
  formatterOrPrefix?: ((key: string) => string) | string
): CssVars<T> => {
  const varNameFormatter = normalizeFormatter(formatterOrPrefix);
  const defs: CssVarDefinitions = {};
  const getters: Record<
    string,
    (defaultVal?: string | number) => string | number
  > = {};

  const refs: Record<string, string> = {};
  const varNames: Record<string, string> = {};

  if (vars) {
    for (const variable in vars) {
      const defaultVal = vars[variable];
      const varName = varNameFormatter(variable);
      varNames[variable] = varName.slice(2);
      let refWithDefault = `var(${varName})`;

      if (typeof defaultVal !== 'undefined') {
        defs[varName] = defaultVal;
        refWithDefault = `var(${varName}, ${defaultVal})`;
      }

      refs[variable] = refWithDefault;

      getters[variable] = (defaultVal?: string | number): string | number => {
        const value = defaultVal ?? vars[variable] ?? '';
        return value ? `var(${varName}, ${value})` : refWithDefault;
      };
    }
  }
  return {
    defs,
    varNames: varNames as Record<keyof T, string>,
    refs: refs as CssVarReferences<T>,
    getters: getters as Record<
      keyof T,
      (defaultValue?: string | number) => CssVarRef
    >,
    setValues: (values, includeDefs): CssVarDefinitions => {
      const result: CssVarDefinitions = {};
      if (includeDefs) {
        for (const variable in defs) {
          const val = defs[variable as keyof typeof defs];
          if (val !== undefined) {
            result[variable as CssVar] = val;
          }
        }
      }
      for (const variable in values) {
        const val = values[variable];
        result[varNameFormatter(variable)] = val as string | number;
      }
      return result;
    },
  };
};

export const cssVarRefToNegative = (cssVar: CssVarRef): `calc${string}` =>
  `calc(${cssVar} * -1)`;
